// Tunable constants. Gameplay/rendering knobs live here so they're easy to find.

import * as THREE from "three";
import { BOUND } from "./map/active.ts";

// Player movement speed (relaxed walk; Shift = run) + the road bonus. The
// numbers live in shared.ts (THREE-free, so the server's move budget and the
// loadtest bots import the same values); re-exported here for existing consumers.
export { WALK_SPEED, RUN_SPEED, ROAD_BOOST } from "./shared.ts";

// Jump/gravity tuning moved to src/sim/vertical.ts (`V`) — the pure home the
// client, the server envelope, and the Skill Sim all read (config.ts imports
// THREE, so the server/sim can't use it).

// How far the player can wander from center before being clamped — the ACTIVE
// map's live value (plan 21; size/2 - 2, re-exported from src/map/active.ts).
export { BOUND };

// Seconds per full day. Hold T to fast-forward by TIME_FF.
export const DAY_LENGTH = 120;
export const TIME_FF = 25;

// Warm point lights at lit structures, ramped at night. Bounded to keep the
// shaders cheap; structures beyond the cap just stay dark.
export const MAX_LIGHTS = 8;
export const LAMP_INTENSITY = 22; // multiplied by the night factor [0,1]
// Lamp occlusion (a hearth must not glow through the house): the nearest
// N lamps cast STATIC, baked cube shadow maps (re-rendered only when a lamp
// moves to a new spot — never per frame); the rest are plain lights. Every
// shadowed lamp costs a cube-map sample per pixel per frame, so the COUNT is
// a quality-preset knob now (GraphicsSettings.lampShadows in settings.ts —
// Low 0 / Medium 2 / High 4); LAMP_SHADOW_MAP is bake resolution.
export const LAMP_SHADOW_MAP = 512;

// Camera (tilted RSC-style, follows the player from behind).
export const FOV = 55;
// Far plane just past FULL fog (fog runs 60→135, and scene.background is kept
// equal to the fog color in daynight.ts) — everything beyond it is already an
// invisible fog-colored silhouette, so clipping it there is visually lossless
// and lets frustum culling drop far chunks/meshes for free. Raise this if the
// fog far distance ever grows.
export const CAM_FAR = 170;
export const CAM_OFFSET = new THREE.Vector3(0, 14, 16); // height + distance behind
export const CAM_ORBIT_SPEED = 1.8; // radians/sec when holding Q/E

// Bloom post-processing knobs (strength, radius, threshold).
export const BLOOM = { strength: 0.55, radius: 0.6, threshold: 0.82 };

// Shirt-color choices on the sign-in screen.
export const SWATCHES = [0x3b6ea5, 0xb5402f, 0x3f8a4a, 0xb58a2f, 0x7a4fb0, 0x2a2e38];

// The rest of the character-creation look (rendered as swatch rows like the
// shirt SWATCHES; first entry of each = the classic hero, matching
// DEFAULT_APPEARANCE in shared.ts).
export const SKIN_SWATCHES = [0xe0ac69, 0xf4d5b8, 0xc68642, 0xa06a3c, 0x8d5524, 0x5c3a21];
export const HAIR_SWATCHES = [0x4a2f1a, 0x1b1512, 0x8a5a2a, 0xc9a227, 0xb0413e, 0x8f8578];
export const EYE_SWATCHES = [0x222028, 0x4a3220, 0x2f5a8f, 0x3f6d3f, 0x6b7280];
export const PANTS_SWATCHES = [0x355070, 0x5a4632, 0x2a2e38, 0x3f5e3f, 0x8a6f4d, 0x6e3b3b];
// The hair-style + body-type lists live in shared.ts (they're part of the
// Appearance type) — re-exported here so the swatch UI keeps one import site
// for all look knobs.
export {
  HAIR_STYLES, BEARD_STYLES, SEXES,
  HAIR_STYLES_BY_SEX, BEARD_STYLES_BY_SEX,
  hairStylesFor, beardStylesFor, fitHairStyle, fitBeardStyle,
} from "./shared.ts";
